import type { ApiClient } from '../../common/apiClient'
import type { TokenInfo, UserContextModel, UserDefinition } from '../types'
import type { AssignUserZonesApiRequest, AssignUserZonesApiResponse } from './types'
import { createApiClient } from '../../common/apiClient'
import defaultFetch from '../../common/fetch'
import { env as defaultEnv } from '../../environment/env'

interface UsersApiClient {
  getUserContext: (tokenInfo: TokenInfo) => Promise<UserContextModel>
  getUsers: (tokenInfo: TokenInfo) => Promise<UserDefinition[]>
  getUser: (userId: string, tokenInfo: TokenInfo) => Promise<UserDefinition>
  assignZones: (userId: string, zonesIds: string[], tokenInfo: TokenInfo) => Promise<AssignUserZonesApiResponse | void>
}

interface Dependencies {
  apiClient: ApiClient
  env: {
    DESTINATIONS_TOOL_BACKEND_BASE_URL: string
  }
}

export function createUsersApiClient(dependencies: Dependencies): UsersApiClient {
  const { apiClient, env } = dependencies

  async function getUserContext(tokenInfo: TokenInfo): Promise<UserContextModel> {
    const userContextModel: UserContextModel | null = await apiClient.apiGet<UserContextModel>(
      `${env.DESTINATIONS_TOOL_BACKEND_BASE_URL}/ALL/destinationstool/users/context`,
      tokenInfo,
    )
    if (userContextModel === null) {
      throw new Error('User context could not be found')
    }
    return userContextModel
  }

  async function getUsers(tokenInfo: TokenInfo): Promise<UserDefinition[]> {
    const users: UserDefinition[] | null = await apiClient.apiGet<UserDefinition[]>(
      `${env.DESTINATIONS_TOOL_BACKEND_BASE_URL}/ALL/destinationstool/users`,
      tokenInfo,
    )
    if (users === null) {
      throw new Error('Users endpoint could not be found')
    }
    return users
  }

  async function getUser(userId: string, tokenInfo: TokenInfo): Promise<UserDefinition> {
    const user: UserDefinition | null = await apiClient.apiGet<UserDefinition>(
      `${env.DESTINATIONS_TOOL_BACKEND_BASE_URL}/ALL/destinationstool/users/${userId}`,
      tokenInfo,
    )
    if (user === null) {
      throw new Error(`User ${userId} could not be found`)
    }
    return user
  }

  async function assignZones(userId: string, zonesIds: string[], tokenInfo: TokenInfo): Promise<AssignUserZonesApiResponse | void> {
    const requestBody: AssignUserZonesApiRequest = {
      zonesIds,
    }

    const assignedUserZones: AssignUserZonesApiResponse = await apiClient.apiPatch<AssignUserZonesApiResponse, AssignUserZonesApiRequest>(
      `${env.DESTINATIONS_TOOL_BACKEND_BASE_URL}/ALL/destinationstool/users/${userId}/zones`,
      requestBody,
      tokenInfo,
    )

    return assignedUserZones
  }

  return {
    getUserContext,
    getUsers,
    getUser,
    assignZones,
  }
}

const dependencies = {
  apiClient: createApiClient({ fetch: defaultFetch as typeof window.fetch, env: defaultEnv }),
  env: defaultEnv,
}

export const usersApiClient = createUsersApiClient(dependencies)
